/**
 * checklist-item-helpers.ts
 *
 * Pure utility functions for working with the evolving checklist `options`
 * shape. Legacy checklists store `options: string[]`; v1+ checklists store
 * `options: ChecklistItem[]` where each item carries a type, description,
 * link, and required flag.
 *
 * Every read path that touches `options` MUST go through `normalizeItem` so
 * legacy data renders identically to today. Ported verbatim from the RN
 * helper at `happily_reactnative_new/src/components/town_hall/
 * checklist_item_helpers.js` to keep web and mobile in lockstep.
 */

export const CHECKLIST_ITEM_TYPES = {
  SIMPLE: 'simple',
  ACKNOWLEDGE: 'acknowledge',
  LINK: 'link',
} as const;

export type ChecklistItemType =
  | typeof CHECKLIST_ITEM_TYPES.SIMPLE
  | typeof CHECKLIST_ITEM_TYPES.ACKNOWLEDGE
  | typeof CHECKLIST_ITEM_TYPES.LINK;

export interface ChecklistItem {
  label: string;
  type: ChecklistItemType;
  required: boolean;
  description?: string;
  link?: string;
}

const VALID_TYPES = new Set<string>(Object.values(CHECKLIST_ITEM_TYPES));

/**
 * Coerce a raw option value (string from legacy data, or object from v1+)
 * into the canonical ChecklistItem shape. Safe to call on any value.
 */
export function normalizeItem(raw: any): ChecklistItem {
  if (raw == null) {
    return { label: '', type: CHECKLIST_ITEM_TYPES.SIMPLE, required: true };
  }

  if (typeof raw === 'string') {
    return { label: raw, type: CHECKLIST_ITEM_TYPES.SIMPLE, required: true };
  }

  if (typeof raw === 'object') {
    const label = typeof raw.label === 'string' ? raw.label : '';
    const type = (VALID_TYPES.has(raw.type) ? raw.type : CHECKLIST_ITEM_TYPES.SIMPLE) as ChecklistItemType;
    const required = raw.required !== false; // default true
    const normalized: ChecklistItem = { label, type, required };
    if (type === CHECKLIST_ITEM_TYPES.ACKNOWLEDGE && typeof raw.description === 'string') {
      normalized.description = raw.description;
    }
    if (type === CHECKLIST_ITEM_TYPES.LINK && typeof raw.link === 'string') {
      normalized.link = raw.link;
    }
    return normalized;
  }

  return { label: String(raw), type: CHECKLIST_ITEM_TYPES.SIMPLE, required: true };
}

/**
 * Map a raw options array to normalized ChecklistItem[]. Returns [] on
 * any non-array input so callers don't have to guard.
 */
export function normalizeItems(options: any): ChecklistItem[] {
  if (!Array.isArray(options)) return [];
  return options.map(normalizeItem);
}

/**
 * Ensure a link string has a scheme prefix. Used before saving so the URL
 * is well-formed when the RN client (or another web view) opens it later.
 */
export function ensureScheme(url: string): string {
  if (typeof url !== 'string' || !url) return '';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^[a-z]+:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Build the write payload a Vuex action / form should send to Firestore.
 * Filters empty labels, strips type-irrelevant fields, ensures link
 * schemes. The result is what lands in `townhall/{id}.options`.
 */
export function toWriteOptions(items: any[]): Array<Record<string, any>> {
  return normalizeItems(items)
    .filter((it) => it.label.trim().length > 0)
    .map((it) => {
      const o: Record<string, any> = {
        label: it.label.trim(),
        type: it.type,
        required: it.required,
      };
      if (it.type === CHECKLIST_ITEM_TYPES.ACKNOWLEDGE && it.description) {
        o.description = it.description;
      }
      if (it.type === CHECKLIST_ITEM_TYPES.LINK && it.link) {
        o.link = ensureScheme(it.link);
      }
      return o;
    });
}
